import { useState, useEffect } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { entityModelerApi } from '../../api/entityModeler';

const C = {
  navy: '#1B2A4A', navyLight: '#2A3D66', navyMuted: '#3A5080', burgundy: '#7A2E3B',
  gold: '#B8953E', hunter: '#2D5F4A', ink: '#1A1A1A',
  textBody: '#3D3830', textMuted: '#7A7265', textLight: '#A69D91',
  ivory: '#FAF7F2', cream: '#F3EDE4', border: '#D6CCBE',
};
const PIE_COLORS = [C.navy, C.burgundy, C.hunter, C.gold, C.navyLight, C.navyMuted];

function fmt(v: number | null | undefined): string {
  if (v == null) return '—';
  const abs = Math.abs(v); const neg = v < 0;
  if (abs >= 1e6) return `${neg ? '(' : ''}$${(abs / 1e6).toFixed(1)}M${neg ? ')' : ''}`;
  if (abs >= 1000) return `${neg ? '(' : ''}$${(abs / 1000).toFixed(0)}K${neg ? ')' : ''}`;
  return `${neg ? '(' : ''}$${abs.toFixed(0)}${neg ? ')' : ''}`;
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="flex-1 min-w-[120px] px-5 py-4 border-r border-stone-200 last:border-r-0">
      <p className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase mb-2">{label}</p>
      <p className="text-2xl font-display font-semibold leading-none" style={{ color: accent || C.ink }}>{value}</p>
      {sub && <p className="text-[11px] font-sans text-stone-500 mt-1.5">{sub}</p>}
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-ivory-50 border border-stone-300 rounded-sm ${className}`}>{children}</div>;
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-[13px] font-sans font-bold text-stone-500 tracking-widest uppercase border-b-2 border-navy-800 inline-block pb-1">{children}</h3>
      {sub && <p className="text-[13px] font-body text-stone-400 italic mt-1.5">{sub}</p>}
    </div>
  );
}

function AlertBox({ type, title, children }: { type: 'danger' | 'warning' | 'success' | 'info'; title?: string; children: React.ReactNode }) {
  const styles: Record<string, { bg: string; border: string; tc: string }> = {
    danger: { bg: 'bg-red-50', border: 'border-l-red-800', tc: 'text-red-800' },
    warning: { bg: 'bg-amber-50', border: 'border-l-amber-600', tc: 'text-amber-700' },
    success: { bg: 'bg-emerald-50', border: 'border-l-emerald-700', tc: 'text-emerald-700' },
    info: { bg: 'bg-blue-50', border: 'border-l-blue-800', tc: 'text-blue-800' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} ${s.border} border-l-[3px] rounded-sm p-3.5 mb-2.5`}>
      {title && <p className={`font-sans text-[13px] font-bold ${s.tc} mb-1`}>{title}</p>}
      <div className="font-body text-[12px] text-stone-700 leading-relaxed">{children}</div>
    </div>
  );
}

function StatusPill({ status, label }: { status: string; label: string }) {
  const map: Record<string, string> = {
    compliant: 'bg-emerald-100 text-emerald-800', warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800', info: 'bg-blue-100 text-blue-800',
  };
  return <span className={`inline-block px-2.5 py-0.5 rounded-sm text-[10px] font-sans font-bold tracking-wide uppercase ${map[status] || map.info}`}>{label}</span>;
}

const ChartTooltip = ({ active, payload }: any) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-ivory-50 border border-stone-300 rounded-sm px-3 py-2 shadow-lg">
      {payload.map((e: any, i: number) => (
        <p key={i} className="text-[12px] font-sans font-medium" style={{ color: e.payload?.fill || C.ink }}>
          {e.name}: {e.value}%
        </p>
      ))}
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────

export default function EntityModelerPage() {
  const [panel, setPanel] = useState<'ownership' | 'events' | 'compliance'>('ownership');
  const [entities, setEntities] = useState<any[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [ownership, setOwnership] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [compliance, setCompliance] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Event form
  const [eventType, setEventType] = useState('contribution');
  const [eventParams, setEventParams] = useState<any>({ contributorMemberId: '', amount: 0, structure: 'preferred' });

  const loadData = async () => {
    setLoading(true);
    try {
      const api = (await import('../../api/client')).default;
      const [entRes, memRes, sumRes] = await Promise.all([
        api.get('/entities'),
        api.get('/members'),
        entityModelerApi.summary().catch(() => ({ data: { data: null } })),
      ]);
      setEntities(entRes.data.data?.rows || entRes.data.data || []);
      setMembers(memRes.data.data?.rows || memRes.data.data || []);
      setSummary(sumRes.data.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const loadEntityData = async (entityId: string) => {
    setSelectedEntityId(entityId);
    if (!entityId) { setOwnership([]); setEvents([]); setCompliance(null); return; }
    try {
      const [ownerRes, eventRes, compRes] = await Promise.all([
        entityModelerApi.ownership(entityId),
        entityModelerApi.events(entityId),
        entityModelerApi.compliance(entityId).catch(() => ({ data: { data: null } })),
      ]);
      setOwnership(ownerRes.data.data || []);
      setEvents(eventRes.data.data || []);
      setCompliance(compRes.data.data);
    } catch (err) { console.error(err); }
  };

  const handleCreateEvent = async () => {
    if (!selectedEntityId) return;
    try {
      await entityModelerApi.createEvent(selectedEntityId, eventType, eventParams);
      loadEntityData(selectedEntityId);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-[14px] font-sans text-stone-400 animate-pulse">Loading entity modeler...</p></div>;

  const panels = [
    { id: 'ownership' as const, label: 'Ownership & Structure' },
    { id: 'events' as const, label: 'Entity Events' },
    { id: 'compliance' as const, label: '§2701 Compliance' },
  ];

  return (
    <div>
      <Card className="flex mb-6 overflow-hidden flex-wrap">
        <StatCard label="Total Entities" value={summary?.total_entities || '0'} accent={C.navy} />
        <StatCard label="Partnerships" value={summary?.partnerships || '0'} sub="LP / LLC" />
        <StatCard label="Trusts" value={summary?.trusts || '0'} sub="Irrevocable / GRAT / IDGT" />
        <StatCard label="Corporations" value={summary?.corporations || '0'} sub="S-Corp / C-Corp" />
        <StatCard label="Events" value={summary?.total_events || '0'} sub={parseInt(summary?.flagged_events || '0') > 0 ? `${summary.flagged_events} flagged` : 'None flagged'} accent={parseInt(summary?.flagged_events || '0') > 0 ? C.gold : C.ink} />
      </Card>

      {/* Entity Selector */}
      <Card className="p-5 mb-5">
        <div className="flex items-center gap-4">
          <label className="text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase">Select Entity</label>
          <select value={selectedEntityId} onChange={(e) => loadEntityData(e.target.value)}
            className="flex-1 max-w-md px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans text-stone-800">
            <option value="">Choose an entity...</option>
            {entities.map((e: any) => <option key={e.id} value={e.id}>{e.name} ({e.entity_type})</option>)}
          </select>
        </div>
      </Card>

      {selectedEntityId && (
        <>
          <div className="flex gap-1 mb-5 bg-stone-200 rounded-sm p-0.5 w-fit">
            {panels.map((p) => (
              <button key={p.id} onClick={() => setPanel(p.id)}
                className={`px-4 py-1.5 rounded-sm border-none cursor-pointer text-[11px] font-sans font-semibold transition-all ${panel === p.id ? 'text-ivory-100' : 'text-stone-500 bg-transparent'}`}
                style={panel === p.id ? { background: C.navy, color: C.ivory } : {}}>{p.label}</button>
            ))}
          </div>

          {/* Ownership */}
          {panel === 'ownership' && (
            <div className="grid grid-cols-2 gap-5">
              <Card className="p-6">
                <SectionTitle>Current Ownership</SectionTitle>
                {ownership.length === 0 ? (
                  <p className="text-[13px] font-sans text-stone-400">No ownership stakes recorded for this entity.</p>
                ) : (
                  <div className="flex gap-5">
                    <ResponsiveContainer width="45%" height={200}>
                      <PieChart>
                        <Pie data={ownership.map((o: any) => ({ name: o.owner_name, value: parseFloat(o.ownership_pct) }))}
                          cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke={C.ivory} strokeWidth={2}>
                          {ownership.map((_: any, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<ChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 flex flex-col justify-center gap-2.5">
                      {ownership.map((o: any, i: number) => (
                        <div key={o.id} className="flex items-center gap-2.5">
                          <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span className="flex-1 text-[12px] font-sans text-stone-600">{o.owner_name}</span>
                          <span className="text-[12px] font-sans text-stone-400">{o.interest_class}</span>
                          <span className="text-[13px] font-mono font-semibold text-stone-800">{parseFloat(o.ownership_pct).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
              <Card className="p-6">
                <SectionTitle>Entity Details</SectionTitle>
                {(() => {
                  const e = entities.find((en: any) => en.id === selectedEntityId);
                  if (!e) return null;
                  return (
                    <div className="space-y-2.5">
                      {[
                        { l: 'Type', v: e.entity_type },
                        { l: 'Domicile', v: e.domicile_state || '—' },
                        { l: 'EIN', v: e.ein || '—' },
                        { l: 'Grantor Trust', v: e.is_grantor_trust ? 'Yes' : 'No' },
                        { l: 'Crummey Powers', v: e.has_crummey_powers ? 'Yes' : 'No' },
                        { l: 'Freeze Discount', v: parseFloat(e.freeze_discount_pct || '0') > 0 ? `${e.freeze_discount_pct}%` : 'None' },
                      ].map((r, i) => (
                        <div key={i} className="flex justify-between py-1.5 border-b border-stone-200">
                          <span className="text-[12px] font-sans text-stone-500">{r.l}</span>
                          <span className="text-[13px] font-sans font-semibold text-stone-800">{r.v}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </Card>
            </div>
          )}

          {/* Events */}
          {panel === 'events' && (
            <div>
              <Card className="p-6 mb-5">
                <SectionTitle>Model an Event</SectionTitle>
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Event Type</label>
                    <select value={eventType} onChange={e => { setEventType(e.target.value); setEventParams({ contributorMemberId: '', amount: 0, structure: 'preferred' }); }}
                      className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans">
                      {['contribution', 'redemption', 'distribution', 'freeze'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Member</label>
                    <select value={eventParams.contributorMemberId || eventParams.memberIdRedeemed || ''} onChange={e => setEventParams({ ...eventParams, contributorMemberId: e.target.value, memberIdRedeemed: e.target.value })}
                      className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans">
                      <option value="">Select...</option>
                      {members.map((m: any) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Amount ($)</label>
                    <input type="number" value={eventParams.amount || eventParams.totalDistribution || eventParams.frozenValue || 0}
                      onChange={e => setEventParams({ ...eventParams, amount: Number(e.target.value), totalDistribution: Number(e.target.value), frozenValue: Number(e.target.value) })}
                      className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono" />
                  </div>
                  {eventType === 'contribution' && (
                    <div>
                      <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Structure</label>
                      <select value={eventParams.structure} onChange={e => setEventParams({ ...eventParams, structure: e.target.value })}
                        className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans">
                        {['dilution', 'preferred', 'loan'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <button onClick={handleCreateEvent} className="px-5 py-2 rounded-sm text-[12px] font-sans font-bold text-ivory-100" style={{ background: C.navy }}>
                  Process Event
                </button>
              </Card>

              <Card className="overflow-hidden">
                <div className="px-6 pt-5 pb-3"><SectionTitle>Event History</SectionTitle></div>
                {events.length === 0 ? (
                  <div className="p-8 text-center"><p className="text-[13px] font-sans text-stone-400">No events recorded for this entity.</p></div>
                ) : (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-ivory-200">
                        {['Date', 'Type', 'Member', 'Amount', 'Gift Tax Impact', '§2701', 'Notes'].map(h => (
                          <th key={h} className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase text-left px-3.5 py-2.5 border-b-2 border-stone-300">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((ev: any, i: number) => (
                        <tr key={ev.id} className={i % 2 === 0 ? '' : 'bg-ivory-200'}>
                          <td className="px-3.5 py-3 text-[13px] font-sans text-stone-800 border-b border-stone-200">{new Date(ev.event_date).toLocaleDateString()}</td>
                          <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200 uppercase">{ev.event_type}</td>
                          <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200">{ev.member_name || '—'}</td>
                          <td className="px-3.5 py-3 text-[13px] font-mono text-stone-800 border-b border-stone-200">{fmt(parseFloat(ev.amount || '0'))}</td>
                          <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200" style={{ color: parseFloat(ev.gift_tax_impact || '0') > 0 ? C.burgundy : C.textLight }}>
                            {parseFloat(ev.gift_tax_impact || '0') > 0 ? fmt(parseFloat(ev.gift_tax_impact)) : '—'}
                          </td>
                          <td className="px-3.5 py-3 border-b border-stone-200">
                            {ev.section_2701_flag ? <StatusPill status="warning" label="Flagged" /> : <span className="text-[11px] text-stone-400">—</span>}
                          </td>
                          <td className="px-3.5 py-3 text-[12px] font-sans text-stone-500 border-b border-stone-200 max-w-xs truncate">{ev.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>
          )}

          {/* §2701 Compliance */}
          {panel === 'compliance' && compliance && (
            <div className="grid grid-cols-2 gap-5">
              <Card className="p-6">
                <SectionTitle>Compliance Status</SectionTitle>
                <div className="flex items-center gap-3 mb-5">
                  <StatusPill status={compliance.isCompliant ? 'compliant' : 'warning'} label={compliance.isCompliant ? 'No Issues' : 'Review Needed'} />
                  <span className="text-[13px] font-sans text-stone-600">{compliance.entityName}</span>
                </div>
                <div className="space-y-2.5">
                  {[
                    { l: 'Entity Type', v: compliance.entityType },
                    { l: 'Preferred Class Exists', v: compliance.hasPreferredClass ? 'Yes' : 'No', c: compliance.hasPreferredClass ? C.gold : C.hunter },
                    { l: 'Freeze Discount Applied', v: compliance.hasFreezeDiscount ? 'Yes' : 'No', c: compliance.hasFreezeDiscount ? C.gold : C.hunter },
                    { l: 'Flagged Events', v: String(compliance.flaggedEventCount), c: compliance.flaggedEventCount > 0 ? C.burgundy : C.hunter },
                  ].map((r, i) => (
                    <div key={i} className="flex justify-between py-1.5 border-b border-stone-200">
                      <span className="text-[12px] font-sans text-stone-500">{r.l}</span>
                      <span className="text-[13px] font-sans font-semibold" style={{ color: r.c || C.ink }}>{r.v}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-6">
                <SectionTitle>Issues & Recommendations</SectionTitle>
                {compliance.issues.length === 0 ? (
                  <AlertBox type="success" title="No §2701 Issues Detected">
                    This entity has no preferred equity classes, freeze discounts, or flagged events requiring §2701 review.
                  </AlertBox>
                ) : (
                  compliance.issues.map((issue: any, i: number) => (
                    <AlertBox key={i} type={issue.severity === 'warning' ? 'warning' : 'info'} title={issue.title}>
                      {issue.detail}
                    </AlertBox>
                  ))
                )}
              </Card>
            </div>
          )}

          {!selectedEntityId && panel !== 'ownership' && (
            <Card className="p-12 text-center">
              <p className="text-[14px] font-sans text-stone-500">Select an entity above to view details</p>
            </Card>
          )}
        </>
      )}

      {!selectedEntityId && (
        <Card className="p-12 text-center">
          <div className="text-stone-300 text-4xl mb-3">🏛</div>
          <p className="text-[14px] font-sans text-stone-500 mb-1">Select an entity to view ownership, model events, and check §2701 compliance</p>
          <p className="text-[12px] font-sans text-stone-400">Entities are created in the Data Management section</p>
        </Card>
      )}
    </div>
  );
}
